import { GENERIC_BINARY_MIME_TYPE } from "../constants/chat";
import type {
  IGetIntegrationRequestMimeTypesProps,
  IResolveIntegrationFileMimeTypeProps,
  IResolveUploadMimeTypeProps,
} from "../types/uploads";
import { getOwnValue } from "./get-own-value";

const getExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1) return "";

  return fileName.slice(lastDotIndex + 1).toLowerCase();
};

// NOTE: `DocumentPicker` reports whatever MIME the document provider hands it,
// and neither platform can be trusted to hand over one the server accepts.
// Android providers answer `application/octet-stream` — or nothing — for
// perfectly valid `.md`, `.json` and legacy Office files. iOS is the opposite
// failure: it answers something *specific* but non-canonical, `text/x-markdown`
// for `.md`, which the server rejects outright.
//
// So a picked value is only trusted once it is canonicalised AND known to be
// accepted; anything else falls back to the extension. Trusting "specific"
// alone was the earlier shape of this function and is exactly what let the iOS
// case through.
//
// The `profile` is what each uploader brings: knowledge and probes advertise
// different formats and validate against different server allow-lists, but the
// picker misreports MIME types the same way on both (NSAI-5017).
//
// NOTE: NSAI-7594 — a browser's `File.type` is no more trustworthy, nor is the
// type a connected app's provider assigns, which is why this moved here from
// the mobile app: the web uploader compared `File.type` to the allow-list
// directly, and Chromium reports `.mkv` as `video/matroska`, a spelling the list
// does not carry. A browser that has no mapping for an extension reports an
// empty string, which takes the same extension fallback as a missing value.
export const resolveUploadMimeType = ({
  fileName,
  profile,
  pickedMimeType,
}: IResolveUploadMimeTypeProps): string => {
  // NOTE: NSAI-5492 — both keys here are untrusted: `pickedMimeType` is
  // whatever the document provider reported, and the extension comes from a
  // file name the user chose. Read with a bare `record[key]` these maps answered
  // for `constructor` and `__proto__` with a function or `Object.prototype`,
  // which is non-nullish — so the `??` fallbacks were skipped and a non-string
  // was returned where this signature promises a string, poisoning the uploaded
  // `mimeType` and every MIME check downstream of it. `getOwnValue` keeps both
  // lookups to the maps' own keys.
  const canonical = pickedMimeType
    ? (getOwnValue({
        record: profile.canonicalMimeType,
        key: pickedMimeType,
      }) ?? pickedMimeType)
    : undefined;

  if (canonical && canonical !== GENERIC_BINARY_MIME_TYPE) {
    if (profile.acceptedMimeTypes.has(canonical)) return canonical;
  }

  return (
    getOwnValue({
      record: profile.mimeTypeByExtension,
      key: getExtension(fileName),
    }) ??
    canonical ??
    GENERIC_BINARY_MIME_TYPE
  );
};

// NOTE: NSAI-7594 — the type a connected-app picker holds a listed file to, or
// null when the host would refuse it. Pickers used to match the provider's type
// against the host's `accept` map exactly, which no provider makes reliable:
// Google Drive keeps whatever type the uploading app sent, OneDrive's Graph has
// been seen answering `application/octet-stream` for `.mkv`, Nextcloud has no
// mapping for `.wave`, and Dropbox reports no type at all. With a `mimeProfile`
// the file is canonicalised exactly like a local pick and judged by the profile,
// so the extension decides when the reported type cannot. Without one it is the
// exact match every picker made before, unchanged for chat and the other hosts.
export const resolveIntegrationFileMimeType = ({
  fileName,
  mimeType,
  accept,
  mimeProfile,
}: IResolveIntegrationFileMimeTypeProps): string | null => {
  if (mimeProfile) {
    const resolved = resolveUploadMimeType({
      fileName,
      profile: mimeProfile,
      pickedMimeType: mimeType,
    });

    return mimeProfile.acceptedMimeTypes.has(resolved) ? resolved : null;
  }

  if (!accept) return mimeType;

  const isAccepted =
    Object.keys(accept).includes(mimeType) ||
    Object.values(accept).flat().includes(mimeType);

  return isAccepted ? mimeType : null;
};

// NOTE: NSAI-7594 — what a picker asks its provider to list. With a
// `mimeProfile` that is every family the profile accepts (`audio/`, `video/`)
// plus the generic binary type: the provider's own type cannot be trusted to be
// one of the profile's spellings, so the provider only narrows the listing and
// `resolveIntegrationFileMimeType` decides per file. A trailing `/` marks a
// family, which every connector's listing endpoint matches as a prefix.
// Without a profile it is the `accept` keys, as before.
export const getIntegrationRequestMimeTypes = ({
  accept,
  mimeProfile,
}: IGetIntegrationRequestMimeTypesProps): string[] => {
  if (!mimeProfile) return Object.keys(accept ?? {});

  const families = new Set(
    [...mimeProfile.acceptedMimeTypes]
      .map((mimeType) => mimeType.slice(0, mimeType.indexOf("/") + 1))
      .filter((family) => family.length > 0),
  );

  return [...families, GENERIC_BINARY_MIME_TYPE];
};
